// Custom hook for automatic scroll-to-top on page changes
// This ensures consistent UX across all page transitions

import { useEffect } from 'react';

/**
 * Custom hook that automatically scrolls to top when the page changes
 * @param page - The current page identifier (any value that changes when page changes)
 * @param options - Configuration options for scroll behavior
 */
export const useScrollToTop = (
    page: string | number | boolean | null | undefined,
    options: {
        behavior?: ScrollBehavior;
        delay?: number;
        enabled?: boolean;
    } = {}
) => {
    const { behavior = 'smooth', delay = 0, enabled = true } = options;

    useEffect(() => {
        if (!enabled) return;

        const scrollToTop = () => {
            window.scrollTo({ 
                top: 0, 
                behavior 
            });
        };

        if (delay > 0) {
            const timeoutId = setTimeout(scrollToTop, delay);
            return () => clearTimeout(timeoutId);
        } else {
            scrollToTop();
        }
    }, [page, behavior, delay, enabled]);
};

/**
 * Hook for immediate scroll-to-top (no dependencies)
 * Useful for manual triggers or one-time scrolls
 */
export const useScrollToTopImmediate = (options: {
    behavior?: ScrollBehavior;
    delay?: number;
} = {}) => {
    const { behavior = 'smooth', delay = 0 } = options;

    const scrollToTop = () => {
        const doScroll = () => {
            window.scrollTo({ top: 0, behavior });
        };

        if (delay > 0) {
            setTimeout(doScroll, delay);
        } else {
            doScroll();
        }
    };

    return scrollToTop;
};

/**
 * Hook for scroll-to-top on component mount
 * Useful for pages that should always start at the top
 */
export const useScrollToTopOnMount = (options: {
    behavior?: ScrollBehavior;
    delay?: number;
    enabled?: boolean;
} = {}) => {
    const { behavior = 'smooth', delay = 0, enabled = true } = options;

    useEffect(() => {
        if (!enabled) return;

        const scrollToTop = () => {
            window.scrollTo({ top: 0, behavior });
        };

        if (delay > 0) {
            const timeoutId = setTimeout(scrollToTop, delay);
            return () => clearTimeout(timeoutId);
        } else {
            scrollToTop();
        }
    }, []); // Empty dependency array - runs only on mount
};

export default useScrollToTop;
